'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import MedicationCard from './MedicationCard';
import ScannerInterface from './ScannerInterface';
import InteractionWarningModal from './InteractionWarningModal';
import FamilyVisibilityPanel from './FamilyVisibilityPanel';
import RefillTracker from './RefillTracker';

interface Medication {
  id: string;
  name: string;
  dosage: string;
  schedule: string;
  lastTaken: string | null;
  nextDose: string;
  refillDate: string;
  refillsRemaining: number;
  prescribedBy: string;
  instructions: string;
  interactions: string[];
  imageUrl: string;
  imageAlt: string;
}

interface FamilyMember {
  id: string;
  name: string;
  avatar: string;
  avatarAlt: string;
  role: string;
  lastViewed: string;
}

interface AdherenceData {
  medicationName: string;
  adherenceRate: number;
  missedDoses: number;
  lastTaken: string;
}

interface RefillItem {
  id: string;
  medicationName: string;
  refillDate: string;
  daysUntilRefill: number;
  pharmacy: string;
  pharmacyPhone: string;
  refillsRemaining: number;
  status: 'urgent' | 'upcoming' | 'scheduled';
}

interface Interaction {
  medication: string;
  severity: 'mild' | 'moderate' | 'severe';
  description: string;
  recommendation: string;
}

export default function MedicationManagerInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [showScanner, setShowScanner] = useState(false);
  const [selectedMedicationId, setSelectedMedicationId] = useState<string | null>(
    null
  );
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'needsRefill'>(
    'all'
  );

  useEffect(() => {
    setIsHydrated(true);

    const mockMedications: Medication[] = [
    {
      id: '1',
      name: 'Lisinopril',
      dosage: '10mg tablet',
      schedule: 'Once daily in morning',
      lastTaken: '2 hours ago',
      nextDose: 'Tomorrow at 8:00 AM',
      refillDate: '03/15/2026',
      refillsRemaining: 3,
      prescribedBy: 'Dr. Robert Chen',
      instructions: 'Take one tablet by mouth once daily in the morning',
      interactions: [
      'May increase potassium levels when combined with Spironolactone'],

      imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_173acfdcf-1764656015228.png",
      imageAlt: 'White round pill bottle with prescription label showing Lisinopril medication'
    },
    {
      id: '2',
      name: 'Metformin',
      dosage: '500mg tablet',
      schedule: 'Twice daily with meals',
      lastTaken: null,
      nextDose: 'Today at 12:00 PM',
      refillDate: '02/28/2026',
      refillsRemaining: 1,
      prescribedBy: 'Dr. Robert Chen',
      instructions: 'Take one tablet by mouth twice daily with breakfast and dinner',
      interactions: [],
      imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd577d62-1764656014822.png",
      imageAlt: 'Orange prescription bottle with white cap containing Metformin tablets'
    },
    {
      id: '3',
      name: 'Atorvastatin',
      dosage: '20mg tablet',
      schedule: 'Once daily at bedtime',
      lastTaken: '12 hours ago',
      nextDose: 'Today at 10:00 PM',
      refillDate: '04/01/2026',
      refillsRemaining: 5,
      prescribedBy: 'Dr. Robert Chen',
      instructions: 'Take one tablet by mouth once daily at bedtime',
      interactions: [
      'Grapefruit juice may increase medication levels',
      'May interact with certain antibiotics'],

      imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1121cae4d-1766846447320.png",
      imageAlt: 'Blue and white pill bottle with prescription label for Atorvastatin cholesterol medication'
    }];


    setMedications(mockMedications);
  }, []);

  const familyMembers: FamilyMember[] = [
  {
    id: '1',
    name: 'Jennifer Martinez',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b23ad375-1763294352665.png",
    avatarAlt: 'Professional headshot of Hispanic woman with long dark hair smiling warmly',
    role: 'Primary Caregiver',
    lastViewed: '5 min ago'
  },
  {
    id: '2',
    name: 'David Chen',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13e452f85-1763294640182.png",
    avatarAlt: 'Professional photo of Caucasian man with short brown hair in business casual attire',
    role: 'Family Member',
    lastViewed: '2 hours ago'
  }];


  const adherenceData: AdherenceData[] = [
  {
    medicationName: 'Lisinopril',
    adherenceRate: 95,
    missedDoses: 1,
    lastTaken: '2 hours ago'
  },
  {
    medicationName: 'Metformin',
    adherenceRate: 78,
    missedDoses: 4,
    lastTaken: 'Yesterday'
  },
  {
    medicationName: 'Atorvastatin',
    adherenceRate: 92,
    missedDoses: 2,
    lastTaken: '12 hours ago'
  }];


  const refillItems: RefillItem[] = [
  {
    id: '2',
    medicationName: 'Metformin',
    refillDate: '02/28/2026',
    daysUntilRefill: 14,
    pharmacy: 'CVS Pharmacy',
    pharmacyPhone: '(555) 123-4567',
    refillsRemaining: 1,
    status: 'urgent'
  },
  {
    id: '1',
    medicationName: 'Lisinopril',
    refillDate: '03/15/2026',
    daysUntilRefill: 29,
    pharmacy: 'Walgreens',
    pharmacyPhone: '(555) 987-6543',
    refillsRemaining: 3,
    status: 'upcoming'
  }];


  const handleLogDose = (id: string) => {
    setMedications((prev) =>
    prev.map((med) =>
    med.id === id ? { ...med, lastTaken: 'Just now' } : med
    )
    );
  };

  const handleViewInteractions = (id: string) => {
    setSelectedMedicationId(id);
    setShowInteractionModal(true);
  };

  const handleRefill = (id: string) => {
    console.log('Requesting refill for medication:', id);
  };

  const handleScanComplete = (result: any) => {
    console.log('Scan complete:', result);
    setShowScanner(false);
  };

  const handleRequestRefill = (id: string) => {
    console.log('Requesting refill for:', id);
  };

  const selectedMedication = medications.find(
    (med) => med.id === selectedMedicationId
  );

  const interactions: Interaction[] = selectedMedication ?
  selectedMedication.interactions.map((interaction, index) => ({
    medication: selectedMedication.name,
    severity: index === 0 ? 'moderate' : 'mild',
    description: interaction,
    recommendation:
    'Consult with your healthcare provider before making any changes to your medication regimen.'
  })) :
  [];

  const filteredMedications = medications.filter((med) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'active') return med.lastTaken !== null;
    if (filterStatus === 'needsRefill') return med.refillsRemaining <= 1;
    return true;
  });

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-64 bg-muted rounded"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </div>);

  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-8">
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
                Medication Manager
              </h1>
              <p className="text-base font-caption text-muted-foreground">
                Track medications, monitor adherence, and coordinate with family
              </p>
            </div>
            <button
              onClick={() => setShowScanner(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground text-base font-caption font-medium hover:bg-primary/90 transition-smooth shadow-warm-sm hover:shadow-warm">
              
              <Icon name="CameraIcon" size={20} />
              Scan Medication
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg text-sm font-caption font-medium transition-smooth ${
              filterStatus === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
              }>
              
              All Medications
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg text-sm font-caption font-medium transition-smooth ${
              filterStatus === 'active' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
              }>
              
              Active Today
            </button>
            <button
              onClick={() => setFilterStatus('needsRefill')}
              className={`px-4 py-2 rounded-lg text-sm font-caption font-medium transition-smooth ${
              filterStatus === 'needsRefill' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
              }>
              
              Needs Refill
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {filteredMedications.length > 0 ?
            filteredMedications.map((medication) =>
            <MedicationCard
              key={medication.id}
              medication={medication}
              onLog={handleLogDose}
              onViewInteractions={handleViewInteractions}
              onRefill={handleRefill} />

            ) :

            <div className="glass rounded-lg p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-sage-50 dark:bg-sage-900/20 flex items-center justify-center mx-auto mb-4">
                  <Icon
                  name="BeakerIcon"
                  size={32}
                  className="text-muted-foreground" />
                
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  No Medications Found
                </h3>
                <p className="text-sm font-caption text-muted-foreground mb-4">
                  {filterStatus === 'all' ? 'Start by scanning or adding your first medication' : 'No medications match the selected filter'}
                </p>
                {filterStatus === 'all' &&
              <button
                onClick={() => setShowScanner(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground text-base font-caption font-medium hover:bg-primary/90 transition-smooth">
                
                    <Icon name="CameraIcon" size={20} />
                    Scan Medication
                  </button>
              }
              </div>
            }
          </div>

          <div className="space-y-6">
            <RefillTracker
              refills={refillItems}
              onRequestRefill={handleRequestRefill} />
            
            <FamilyVisibilityPanel
              familyMembers={familyMembers}
              adherenceData={adherenceData} />
            
          </div>
        </div>
      </div>

      {showScanner &&
      <ScannerInterface
        onScanComplete={handleScanComplete}
        onCancel={() => setShowScanner(false)} />

      }

      {showInteractionModal && selectedMedication &&
      <InteractionWarningModal
        medicationName={selectedMedication.name}
        interactions={interactions}
        onClose={() => setShowInteractionModal(false)} />

      }
    </>);

}